import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { BehaviorSubject, Observable, of } from 'rxjs';
import { catchError, map, take } from 'rxjs/operators';
import { environment } from 'src/environments/environment';
import { UserAuthService } from '../UserAuth.service';
import {
  CompanyPreferences,
  DEFAULT_COMPANY_PREFERENCES,
  DEFAULT_EFFECTIVE_PREFERENCES,
  EffectivePreferences,
  PreferencesApiResponse,
  UserPreferences,
} from './preferences.types';

@Injectable({
  providedIn: 'root',
})
export class PreferencesService {
  effective$ = new BehaviorSubject<EffectivePreferences>(DEFAULT_EFFECTIVE_PREFERENCES);
  company$ = new BehaviorSubject<CompanyPreferences>(DEFAULT_COMPANY_PREFERENCES);
  user$ = new BehaviorSubject<UserPreferences>({
    language: null,
    measurementSystem: null,
    theme: null,
    rowsPerPage: null,
    temp3DRange: null,
    timeIn3DGraph: null,
    chartsColorPalette: null,
    chartsAutoRefreshIntervalS: null,
    projectListColumnOrder: null,
    projectListDefaultStatusFilter: null,
    projectListDefaultDateRange: null,
    wizardTemplateId: null,
    exportsDefaultTemplate: null,
    exportsDefaultPdfSections: null,
    fiberTimer: null,
    notificationsEmailEnabled: null,
    notificationsRealtimeAlerts: null,
    notifyOnPreferenceOverwrite: null,
    cureDefaultSheetId: null,
  });
  loadError$ = new BehaviorSubject<string | null>(null);

  private loaded = false;

  constructor(
    private http: HttpClient,
    private userAuth: UserAuthService,
  ) {
    this.hydrateFromCache();
  }

  load(): void {
    const tenantId = this.userAuth.getCurrentCustomerTenantId();
    const objectId = this.getObjectId();
    const customerId = this.userAuth.getActiveCustomerId();

    if (!tenantId || !objectId) {
      return;
    }

    let params = new HttpParams()
      .set('tenantId', tenantId)
      .set('objectId', objectId)
      .set('code', environment.apiKey);
    if (customerId) {
      params = params.set('customerId', customerId);
    }

    this.http
      .get<PreferencesApiResponse>(`${environment.apiUrl}v1/preferences`, { params })
      .pipe(
        catchError((err) => {
          console.warn('PreferencesService > load failed, using cached values', err);
          this.loadError$.next('Failed to load preferences. Using cached values.');
          return of(null);
        })
      )
      .subscribe((response) => {
        if (!response) return;
        this.effective$.next(response.effective);
        this.company$.next(response.company);
        this.user$.next(response.user);
        this.loadError$.next(null);
        this.writeCache(response.effective, tenantId, objectId);
        this.loaded = true;
      });
  }

  saveUser(patch: Partial<UserPreferences>): Observable<EffectivePreferences> {
    const tenantId = this.userAuth.getCurrentCustomerTenantId();
    const objectId = this.getObjectId();
    const customerId = this.userAuth.getActiveCustomerId();
    const fieldUpdatedAt: Record<string, string> = {};
    const now = new Date().toISOString();
    Object.keys(patch).forEach((k) => (fieldUpdatedAt[k] = now));

    // Optimistic update: apply non-null patch values onto the current effective state.
    const optimisticEffective: EffectivePreferences = { ...this.effective$.value };
    (Object.keys(patch) as Array<keyof UserPreferences>).forEach((key) => {
      const val = patch[key];
      if (val != null) (optimisticEffective as any)[key] = val;
    });
    this.effective$.next(optimisticEffective);
    this.writeCache(optimisticEffective, tenantId, objectId);

    const params = new HttpParams()
      .set('code', environment.apiKey);

    return this.http
      .put<PreferencesApiResponse>(
        `${environment.apiUrl}v1/preferences/user`,
        { tenantId, customerId, objectId, patch, fieldUpdatedAt },
        { params }
      )
      .pipe(
        map((response) => {
          this.effective$.next(response.effective);
          this.user$.next(response.user);
          this.writeCache(response.effective, tenantId, objectId);
          return response.effective;
        }),
        catchError((err) => {
          console.error('PreferencesService > saveUser failed', err);
          throw err;
        })
      );
  }

  saveCompany(patch: Partial<CompanyPreferences>): Observable<EffectivePreferences> {
    const tenantId = this.userAuth.getCurrentCustomerTenantId();
    const objectId = this.getObjectId();
    const customerId = this.userAuth.getActiveCustomerId();
    const fieldUpdatedAt: Record<string, string> = {};
    const now = new Date().toISOString();
    Object.keys(patch).forEach((k) => (fieldUpdatedAt[k] = now));

    const params = new HttpParams()
      .set('code', environment.apiKey);

    return this.http
      .put<PreferencesApiResponse>(
        `${environment.apiUrl}v1/preferences/company`,
        { tenantId, customerId, objectId, patch, fieldUpdatedAt },
        { params }
      )
      .pipe(
        map((response) => {
          this.effective$.next(response.effective);
          this.company$.next(response.company);
          this.writeCache(response.effective, tenantId, objectId);
          return response.effective;
        }),
        catchError((err) => {
          console.error('PreferencesService > saveCompany failed', err);
          throw err;
        })
      );
  }

  uploadLogo(file: File): Observable<string> {
    const tenantId = this.userAuth.getCurrentCustomerTenantId();
    const objectId = this.getObjectId();
    const customerId = this.userAuth.getActiveCustomerId();
    const formData = new FormData();
    formData.append('logo', file);

    let params = new HttpParams()
      .set('tenantId', tenantId)
      .set('objectId', objectId)
      .set('code', environment.apiKey);
    if (customerId) {
      params = params.set('customerId', customerId);
    }

    return this.http
      .post<{ brandLogoUrl: string }>(
        `${environment.apiUrl}v1/preferences/company/logo`,
        formData,
        { params }
      )
      .pipe(
        map((response) => {
          const updated = { ...this.company$.value, brandLogoUrl: response.brandLogoUrl };
          this.company$.next(updated);
          this.effective$.next({ ...this.effective$.value, brandLogoUrl: response.brandLogoUrl });
          return response.brandLogoUrl;
        }),
        catchError((err) => {
          console.error('PreferencesService > uploadLogo failed', err);
          throw err;
        })
      );
  }

  refresh(): void {
    this.loaded = false;
    this.load();
  }

  private hydrateFromCache(): void {
    const tenantId = this.userAuth.getCurrentCustomerTenantId();
    const objectId = this.getObjectId();
    if (!tenantId || !objectId) return;

    const cached = localStorage.getItem(this.cacheKey(tenantId, objectId));
    if (cached) {
      try {
        const parsed = JSON.parse(cached) as EffectivePreferences;
        this.effective$.next(parsed);
      } catch {
        // ignore malformed cache
      }
    }
  }

  private writeCache(effective: EffectivePreferences, tenantId: string, objectId: string): void {
    try {
      localStorage.setItem(this.cacheKey(tenantId, objectId), JSON.stringify(effective));
    } catch {
      // ignore storage errors (e.g. private browsing quota)
    }
  }

  // Cache key is scoped by customer so two customers sharing a tenant don't share cache.
  private cacheKey(tenantId: string, objectId: string): string {
    const customerId = this.userAuth.getActiveCustomerId();
    return customerId
      ? `prefs:${tenantId}:${customerId}:${objectId}`
      : `prefs:${tenantId}:${objectId}`;
  }

  private getObjectId(): string {
    return this.userAuth.getObjectId();
  }
}
